import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PayflowHostedCardFrame, {
  type PayflowHostedMessage,
} from '../../Components/PayflowHostedCardFrame';
import {
  completePayflowSession,
  createPayflowSession,
  type CreatePayflowSessionResponse,
} from '../../endpoints/loans';
import { getApiErrorMessage } from '../../utils/httpClientTs';

type ElfLoanAccountOnFileDialogProps = {
  open: boolean;
  onClose: () => void;
  borrowerUserId: string;
  borrowerName?: string;
  /** Default monthly recurring amount (e.g. next expected receivable) */
  defaultAmount?: number | null;
  onSuccess: () => void;
};

const nextBillingStart = (day: number): string => {
  const today = new Date();
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const d = today.getUTCDate();
  let year = y;
  let month = m;
  if (d >= day) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const useDay = Math.min(day, last);
  const dt = new Date(Date.UTC(year, month, useDay));
  return dt.toISOString().slice(0, 10);
};

const ElfLoanAccountOnFileDialog: React.FC<ElfLoanAccountOnFileDialogProps> = ({
  open,
  onClose,
  borrowerUserId,
  borrowerName,
  defaultAmount,
  onSuccess,
}) => {
  const [amount, setAmount] = useState('');
  const [billingDay, setBillingDay] = useState('15');
  const [startDate, setStartDate] = useState(nextBillingStart(15));
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [session, setSession] = useState<CreatePayflowSessionResponse | null>(
    null
  );
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPending(false);
    setCompleting(false);
    setSession(null);
    const day = 15;
    setBillingDay(String(day));
    setStartDate(nextBillingStart(day));
    if (
      defaultAmount != null &&
      !Number.isNaN(defaultAmount) &&
      defaultAmount > 0
    ) {
      setAmount(defaultAmount.toFixed(2));
    } else {
      setAmount('');
    }
  }, [open, defaultAmount]);

  const parsedAmount = Number.parseFloat(amount);
  const parsedDay = Number.parseInt(billingDay, 10);
  const canStart =
    Number.isFinite(parsedAmount) &&
    parsedAmount > 0 &&
    Number.isFinite(parsedDay) &&
    parsedDay >= 1 &&
    parsedDay <= 28 &&
    !!startDate;

  const handleStart = async () => {
    setError(null);
    if (!canStart) {
      setError('Enter amount, billing day (1–28), and start date.');
      return;
    }
    setPending(true);
    try {
      const resp = await createPayflowSession({
        user_id: borrowerUserId,
        purpose: 'account_on_file',
        amount: Math.round(parsedAmount * 100) / 100,
        billing_day: parsedDay,
        start_date: startDate,
      });
      setSession(resp);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const finalize = async () => {
    if (!session || completing) return;
    setCompleting(true);
    setError(null);
    try {
      await completePayflowSession(session.session_id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCompleting(false);
    }
  };

  const onHostedMessage = (msg: PayflowHostedMessage) => {
    if (msg.status === 'approved') {
      void finalize();
      return;
    }
    if (msg.status === 'canceled') {
      setError('Card entry was canceled.');
      setSession(null);
      return;
    }
    setError('Card was not approved. You can try again.');
    setSession(null);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !pending && !completing && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add card on file</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {borrowerName
            ? `Enroll a recurring PayPal card profile for ${borrowerName}.`
            : 'Enroll a recurring PayPal card profile.'}{' '}
          A $1 authorization validates the card and is voided after the profile
          is created. No installment is charged now.
        </Typography>

        {!session ? (
          <Stack spacing={2}>
            <TextField
              label="Recurring amount"
              type="number"
              size="small"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Billing day of month"
              type="number"
              size="small"
              fullWidth
              value={billingDay}
              onChange={(e) => {
                setBillingDay(e.target.value);
                const d = Number.parseInt(e.target.value, 10);
                if (d >= 1 && d <= 28) {
                  setStartDate(nextBillingStart(d));
                }
              }}
              inputProps={{ min: 1, max: 28 }}
              helperText="Day 1–28 each month"
            />
            <TextField
              label="First billing date"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        ) : (
          <PayflowHostedCardFrame
            iframeUrl={session.iframe_url}
            sessionId={session.session_id}
            onMessage={onHostedMessage}
          />
        )}
        {completing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Creating recurring profile…
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending || completing}>
          Cancel
        </Button>
        {!session ? (
          <Button
            variant="contained"
            disabled={!canStart || pending}
            onClick={() => void handleStart()}
          >
            {pending ? 'Preparing…' : 'Continue to card entry'}
          </Button>
        ) : (
          <Button
            variant="outlined"
            disabled={completing}
            onClick={() => setSession(null)}
          >
            Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ElfLoanAccountOnFileDialog;
